import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import MainLayout from './components/MainLayout';
import ProfilePage from './Pages/ProfilePage';
import LoginPage from './Pages/LoginPage';
import ProtectedRoute from './components/ProtectedRoute';
import AuthTokenProvider from './components/AuthTokenProvider';
import SubscriptionNeededPage from './Pages/SubscriptionNeededPage';
import SubscriptionSuccessfulPage from './Pages/SubscriptionSuccessfulPage';
import PropertyViewPage from './Pages/PropertyViewPage';
import PropertyListPage from './Pages/PropertyListPage';
import { SOWListPage } from './Pages/SOW/SOWListPage';
import { VideoIntakeReviewPage } from './Pages/SOW/VideoIntakeReviewPage';
import { SOWGenerationPage } from './Pages/SOW/SOWGenerationPage';
import { SOWOverviewPage } from './Pages/SOW/SOWOverviewPage';
import { SOWChecklistPage } from './Pages/SOW/SOWChecklistPage';
import { WorkItemRefinementPage } from './Pages/SOW/WorkItemRefinementPage';
import { QuestionnaireRefinementPage } from './Pages/SOW/QuestionnaireRefinementPage';
import { BidsPage } from './Pages/BidsPage';
import { BidDetailPage } from './Pages/BidDetailPage';
import { AdminPage } from './Pages/AdminPage';
import AdminRoute from './components/AdminRoute';
import { PublicLandingPage } from './Pages/PublicLandingPage';
import { ScopeReviewPage } from './Pages/SOW/ScopeReviewPage';
import InspectionEvaluationHome from './Pages/InspectionEvaluationHome';
import InspectionEvaluation from './Pages/InspectionEvaluation';
import { LoadScript } from '@react-google-maps/api';

const App: React.FC = () => {
  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN || ''}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID || ''}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: process.env.REACT_APP_AUTH0_AUDIENCE || ''
      }}
    >
      <AuthTokenProvider>
        <LoadScript
          googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
          libraries={['places']}
        >
          <Router>
            <Routes>
              {/* Public routes (no auth, no nav) */}
              <Route path="/scope/:alias/new" element={<PublicLandingPage />} />
              <Route path="/scope/:sowId/wizard" element={<SOWGenerationPage />} />
              <Route path="/scope/:sowId" element={<SOWOverviewPage />} />
              <Route path="/scope/:sowId/questionnaire" element={<SOWChecklistPage />} />
              <Route path="/scope/:sowId/review" element={<ScopeReviewPage />} />

              {/* Authenticated routes with top nav */}
              <Route path="/*" element={
                <>
                  <MainLayout />
                  <div className="pt-14 min-h-screen bg-gray-50">
                    <Routes>
                      {/* Default to Projects */}
                      <Route path="/" element={<Navigate to="/sow" replace />} />
                      <Route path="/welcome" element={<Navigate to="/sow" replace />} />
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/profile" element={<ProtectedRoute component={ProfilePage} />} />
                      <Route path="/subscription" element={<ProtectedRoute component={SubscriptionNeededPage} />} />
                      <Route path="/success" element={<ProtectedRoute component={SubscriptionSuccessfulPage} />} />

                      {/* Projects (SOW) */}
                      <Route path="/sow" element={<ProtectedRoute component={SOWListPage} />} />
                      <Route path="/sow/video-intake" element={<ProtectedRoute component={VideoIntakeReviewPage} />} />
                      <Route path="/sow/:sowId" element={<ProtectedRoute component={SOWOverviewPage} />} />
                      <Route path="/sow/:sowId/questionnaire" element={<ProtectedRoute component={SOWChecklistPage} />} />
                      <Route path="/sow/:sowId/questionnaire/:questionnaireId" element={<ProtectedRoute component={QuestionnaireRefinementPage} />} />
                      <Route path="/sow/:sowId/workitem/:workItemId/refinement/:conversationId?" element={<ProtectedRoute component={WorkItemRefinementPage} />} />
                      <Route path="/sow/:sowId/wizard" element={<ProtectedRoute component={SOWGenerationPage} />} />
                      <Route path="/sow/:sowId/review" element={<ProtectedRoute component={ScopeReviewPage} />} />

                      {/* Bids */}
                      <Route path="/bids" element={<ProtectedRoute component={BidsPage} />} />
                      <Route path="/bids/:bidId" element={<ProtectedRoute component={BidDetailPage} />} />

                      {/* Inspections — routes to property pages */}
                      <Route path="/inspections" element={<ProtectedRoute component={PropertyListPage} />} />
                      <Route path="/inspections/:propertyId/*" element={<ProtectedRoute component={PropertyViewPage} />} />

                      {/* Legacy routes — keep accessible */}
                      <Route path="/property/:propertyId/*" element={<ProtectedRoute component={PropertyViewPage} />} />
                      <Route path="/property" element={<ProtectedRoute component={PropertyListPage} />} />
                      <Route path="/inspection/:inspectionId" element={<ProtectedRoute component={InspectionEvaluation} />} />

                      {/* Admin */}
                      <Route path="/admin" element={<AdminRoute component={AdminPage} />} />
                    </Routes>
                  </div>
                </>
              } />
            </Routes>
          </Router>
        </LoadScript>
      </AuthTokenProvider>
    </Auth0Provider>
  );
};

export default App;
